import React, { useCallback, useEffect, useState, useRef, memo } from 'react';
import {
    Map,
    useMap,
    type MapMouseEvent,
    ColorScheme
} from '@vis.gl/react-google-maps';
import MarkersComponent from '@/components/map/Markers';
import PotentialMarkersComponent from '@/components/map/PotentialMarkers';
import MapHandler from '@/components/map/MapHandler';
import MapSidebar from '@/components/map/Sidebar';
import { type LocationType, type PotentialLocation } from '@/types/firestore';
import { GripVertical, Loader2 } from "lucide-react";
import AlgoliaAdminBox from '@/components/map/AlgoliaAdminBox';
import { GeoPoint, Timestamp } from 'firebase/firestore';
import { PermissionGuard } from '@/components/auth/PermissionGuard';
import MapInfoWindowController from '@/components/map/MapInfoWindowController';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { SerializedError } from '@reduxjs/toolkit';
import {
    selectLocationsLoading,
    selectLocationsError,
    initializeLocationsListener,
    cleanupLocationsListener,
    setListenerInitialized,
    fetchLocations
} from '@/store/slices/locationsSlice';
import {
    selectSelectedMapId,
    initializePublicMapsListener,
    // selectCurrentMapLocationIds // Not strictly needed if visibleGeoJsonPoints is correctly filtered
} from '@/store/slices/customMapsSlice';
import { selectVisibleGeoJsonPoints } from '@/store/selectors/mapSelectors';
import {
    setActiveInfoWindowId,
    addPotentialLocation,
    selectActiveInfoWindowId,
    selectPotentialLocations,
    selectMapBounds,
    selectMapZoom,
    selectMapSelectionTriggeredByUrl,
    setMapSelectionTriggeredByUrl,
} from '@/store/slices/mapUiSlice';
import { useTheme as useNextTheme } from 'next-themes';
import { useThemeInfo } from '@/context/ThemeContext';
import { useAuth } from '@/hooks/useAuth';

// --- Type Augmentation / Reminder --- 
// TODO: Ensure PotentialLocation in src/types/firestore.ts includes these optional fields:
/*
declare module '@/types/firestore' {
  interface PotentialLocation {
    googlePlaceId?: string;
    createdAt?: Timestamp; // Added in handleMapClick
  }
}
*/

// --- Temporary Prop Types for Child Components --- 
// TODO: Move these prop definitions to the actual component files
const MemoizedMarkersComponent = memo(MarkersComponent);

const MemoizedPotentialMarkersComponent = memo(PotentialMarkersComponent);
const MemoizedMapHandler = memo(MapHandler);
const MemoizedAlgoliaAdminBox = memo(AlgoliaAdminBox);
const MemoizedMapInfoWindowController = memo(MapInfoWindowController);

// Helper function to calculate center from bounds
const getCenterFromBounds = (bounds: google.maps.LatLngBoundsLiteral | null): { lat: number; lng: number } | null => {
    if (!bounds) return null;
    return {
        lat: (bounds.north + bounds.south) / 2,
        lng: (bounds.east + bounds.west) / 2,
    };
};

// --- Main Map Page Component ---
const MapPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAuth();

  // Get locations state from RTK store
  const isLoading = useAppSelector(selectLocationsLoading);
  const locationsError = useAppSelector(selectLocationsError);
  
  const { theme: activeThemeName } = useNextTheme();
  const { availableThemes } = useThemeInfo();

  useEffect(() => {
    console.log('[MapPage] Initializing locations listener');
    dispatch(initializeLocationsListener()); // Initialize the listener
    
    // Initialize public maps listener with user ID
    dispatch(initializePublicMapsListener(user?.uid || null));
    
    // Add debug logging for loading state
    console.log(`[MapPage] Initial loading state: ${isLoading}`);

    return () => {
      console.log('[MapPage] Cleaning up locations listener');
      cleanupLocationsListener(); // Call the direct cleanup function
      dispatch(setListenerInitialized(false)); // Dispatch action to update Redux state
    };
  }, [dispatch, user?.uid]);

  // Map UI state (formerly Zustand)
  const activeInfoWindowId = useAppSelector(selectActiveInfoWindowId);
  const potentialLocations = useAppSelector(selectPotentialLocations);
  const mapBounds = useAppSelector(selectMapBounds);
  const mapZoom = useAppSelector(selectMapZoom);
  const selectedMapId = useAppSelector(selectSelectedMapId);
  const visibleGeoJsonPoints = useAppSelector(selectVisibleGeoJsonPoints);
  const mapSelectionTriggeredByUrl = useAppSelector(selectMapSelectionTriggeredByUrl);

  // Map instance and PlacesService setup
  const map = useMap();
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService | null>(null);
  const [mapOptions, setMapOptions] = useState<{ colorScheme?: ColorScheme }>({ colorScheme: undefined });
  const [mapKey, setMapKey] = useState<string>('initialKey');
  // Add loading timeout state
  const [forceHideLoader, setForceHideLoader] = useState<boolean>(false);

  // Timeout to hide loader
  useEffect(() => {
    // Add a reasonable timeout to hide loader if things get stuck
    const loaderTimeout = setTimeout(() => {
      setForceHideLoader(true);
      console.log("[MapPage] Force-hiding loader after timeout");
    }, 8000); // 8 seconds should be enough for most map loads
    
    return () => clearTimeout(loaderTimeout);
  }, []);

  // Add a useEffect to log state changes relevant to the loading condition
  useEffect(() => {
    console.log(`[MapPage] Loading condition values - isLoading: ${isLoading}, potentialLocations: ${potentialLocations.length}, forceHideLoader: ${forceHideLoader}`);
    
    if (isLoading === 'succeeded') {
      console.log('[MapPage] Locations loading succeeded, hiding loader');
      setForceHideLoader(true);
    }
  }, [isLoading, potentialLocations.length, forceHideLoader]);

  // Logging for debugging
  useEffect(() => {
    if (selectedMapId) {
      console.log(`[MapPage] Selected Map ID: ${selectedMapId}`);
      console.log(`[MapPage] Visible GeoJSON Points: ${visibleGeoJsonPoints.length}`);
    }
  }, [selectedMapId, visibleGeoJsonPoints]);

  useEffect(() => {
    if (map) {
      console.log("[MapEffect] Map instance available, initializing PlacesService.");
      setPlacesService(new google.maps.places.PlacesService(map));
      // When map is ready, we can hide the loader
      setForceHideLoader(true);
    } else {
        console.log("[MapEffect] Map instance not yet available.");
        setPlacesService(null);
    }
  }, [map]);

  // Effect to update mapOptions and mapKey when theme changes
  useEffect(() => {
    const setupMapOptions = async () => {
      try {
        const currentThemeDetails = availableThemes.find(t => t.value === activeThemeName);
        const isDark = currentThemeDetails?.isDark ?? false; // Default to light
        
        // Use ColorScheme enum values
        setMapOptions(prevOptions => ({
          ...prevOptions, 
          colorScheme: isDark ? ColorScheme.DARK : ColorScheme.LIGHT,
        }));
        
        // Use a unique key to force re-render when theme changes
        setMapKey(`theme-${isDark ? 'dark' : 'light'}-${Date.now()}`);
        console.log(`[MapPage] Setting colorScheme to: ${isDark ? 'dark' : 'light'}`);

      } catch (e) {
        console.error("[MapPage] Failed to set colorScheme:", e);
        // Fallback: ensure mapKey changes to attempt re-render even on error
        setMapKey('errorKey-' + Date.now());
        setMapOptions(prevOptions => ({
          ...prevOptions,
          colorScheme: undefined, // Explicitly set to undefined on error
        }));
      }
    };

    if (activeThemeName) { // Only run if a theme name is available
        setupMapOptions();
    }
  }, [activeThemeName, availableThemes]); // Rerun when theme name or availableThemes definition changes

  // Effect to fit bounds when a shared/selected map's locations are loaded
  useEffect(() => {
    if (!map || !selectedMapId || !visibleGeoJsonPoints) {
      return; // Exit early if map instance, selected map ID, or points data is missing
    }

    // Only do bounds-fitting when selection was URL-triggered and we have visible points
    if (mapSelectionTriggeredByUrl && visibleGeoJsonPoints.length > 0) {
      console.log(`[MapPage] Fitting bounds to ${visibleGeoJsonPoints.length} points for URL-triggered map selection`);
      
      const bounds = new google.maps.LatLngBounds();
      visibleGeoJsonPoints.forEach(point => {
        const [lng, lat] = point.geometry.coordinates; // GeoJSON order: [lng, lat]
        bounds.extend(new google.maps.LatLng(lat, lng)); // Google Maps LatLng: (lat, lng)
      });

      if (!bounds.isEmpty()) {
        // Schedule a slight delay to ensure the map is ready
        setTimeout(() => {
          if (map) {
            map.fitBounds(bounds);
            console.log('[MapPage] fitBounds called');

            // Add some padding or adjust zoom, especially for single points or very tight fits.
            // This uses an 'idle' listener to ensure the map has settled after fitBounds.
            const listener = map.addListener('idle', () => {
              console.log(`[MapPage] Map idle event triggered. Current zoom: ${map.getZoom()}`);
              if (visibleGeoJsonPoints.length === 1) {
                // If only one point, don't zoom in too aggressively.
                map.setZoom(Math.min(map.getZoom() ?? 15, 15)); 
                console.log(`[MapPage] Zoom adjusted for single point: ${map.getZoom()}`);
              } else if ((map.getZoom() ?? 0) > 17) { 
                // Cap maximum zoom if fitBounds results in a very high zoom level for multiple points.
                map.setZoom(17);
                console.log(`[MapPage] Zoom reduced to avoid over-zooming: ${map.getZoom()}`);
              }
              // Important: Remove the listener after it runs to prevent multiple executions.
              google.maps.event.removeListener(listener);
            });
          }
        }, 100); // Short delay to ensure map is ready
      }
      
      // Reset the flag after fitting bounds so subsequent manual selections don't trigger this effect
      dispatch(setMapSelectionTriggeredByUrl(false));
    } else {
      console.log(`[MapPage] Not fitting bounds. URL-triggered: ${mapSelectionTriggeredByUrl}, Visible points: ${visibleGeoJsonPoints.length}`);
    }
  }, [map, selectedMapId, visibleGeoJsonPoints, mapSelectionTriggeredByUrl, dispatch]);

  // Sidebar state and refs
  const [sidebarWidth, setSidebarWidth] = useState(340);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Resize handlers (copied from old code)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const newWidth = e.clientX - containerRect.left;
    if (newWidth >= 200 && newWidth <= 700) { // Keep constraints
      setSidebarWidth(newWidth);
    }
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Mouse move/up listeners (copied from old code)
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Map click handler - Updated
  const handleMapClick = useCallback(
    (event: MapMouseEvent) => {
      if (activeInfoWindowId !== null) {
        console.log("[MapClick] Info window open, closing it.");
        dispatch(setActiveInfoWindowId(null));
        event.stop();
        return;
      }

      const placeId = event.detail?.placeId;

      // --- 1. Check for POI Click ---
      if (placeId) {

        // Stop Google Maps' default info window from appearing for this POI click
        event.stop();

        if (!placesService) {
          console.error("PlacesService not initialized.");
          return;
        }

        // Check if already a potential location (using RTK state)
        // Assumes PotentialLocation has googlePlaceId
        const existingPotential = potentialLocations.find((p: PotentialLocation) => p.googlePlaceId === placeId);
        if (existingPotential?.id) {
            console.log("[MapClick] Activating existing potential location:", existingPotential.id);
            dispatch(setActiveInfoWindowId(existingPotential.id)); // Dispatch action
            if (map && existingPotential.coordinates) {
                // Coordinates must be GeoPoint according to PotentialLocation type
                let lat = 0;
                let lng = 0;
                if (existingPotential.coordinates instanceof GeoPoint) {
                   lat = existingPotential.coordinates.latitude;
                   lng = existingPotential.coordinates.longitude;
                }
                // Pan only if valid coordinates were extracted
                if (lat !== 0 || lng !== 0) { 
                   map.panTo({ lat, lng });
                }
            }
            return; // Don't fetch details again if it's already potential
        }

        // Fetch details from Places API for a new potential location
        console.log("[MapClick] Requesting POI details...");
        placesService.getDetails({
            placeId: placeId,
            fields: ['name', 'formatted_address', 'geometry.location', 'place_id', 'types']
        }, (place, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location && place.place_id) {
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                const address = place.formatted_address ?? "Address unavailable";

                let locationType: LocationType = 'other';
                if (place.types?.includes('restaurant')) locationType = 'restaurant';

                // Ensure PotentialLocation type includes googlePlaceId
                const newPotentialLocation: PotentialLocation = {
                    id: `potential-${place.place_id}-${Date.now()}`,
                    googlePlaceId: place.place_id,
                    name: place.name || "Unknown Place",
                    address: address,
                    coordinates: { latitude: lat, longitude: lng },
                    locationType: locationType,
                    createdAt: Timestamp.now().toMillis(), // Add timestamp as milliseconds
                };
                console.log("[MapClick] Adding new potential location:", newPotentialLocation);
                // Dispatch action to add/activate
                dispatch(addPotentialLocation({ location: newPotentialLocation, activate: true }));
                if (map) map.panTo({ lat, lng });
            } else {
                console.error("Failed to get place details:", status);
            }
        });
        return; // Stop processing after handling POI click
      }

      // --- 2. Click on map background (or custom marker/info window - library might handle propagation) ---
      // Check if the click target suggests it was *not* on the background.
      // This depends heavily on the DOM structure of your markers/info windows.
      const target = event.domEvent?.target as HTMLElement | undefined;
      if (target?.closest && (target.closest('.custom-marker-class') || target.closest('.custom-infowindow-class'))) {
          // If you have specific classes on your markers/windows, add them here.
          console.log("[MapClick] Click likely on custom element, ignoring.");
          return;
      }


      // --- 3. Click was likely on the map background ---
      console.log("[MapClick] Clicked on map background.");
      // The activeInfoWindowId check here is now redundant if the top-level check handles it.
      // However, this part of the code is only reached if no POI was clicked AND no info window was initially open.
      // So, if activeInfoWindowId became non-null through some other means between the top check and here (unlikely in this flow),
      // this would still close it. For now, the initial top-level check should suffice.
      // if (activeInfoWindowId !== null) {
      //   console.log("[MapClick] Closing info window (background click).");
      //   dispatch(setActiveInfoWindowId(null));
      // }

    }, [dispatch, placesService, map, potentialLocations, activeInfoWindowId]);

  // --- Moved Hooks Before Conditional Return --- 
  // Handle manual refresh - uses one-time fetch
  const handleManualRefresh = useCallback(() => {
    console.log("[Map] Manual refresh requested");
    dispatch(fetchLocations(undefined)); // Re-dispatch fetch action
  }, [dispatch]);

  // Format Error Message
  const getErrorMessage = useCallback((error: SerializedError | string | null): string => {
      if (!error) return 'Unknown error';
      if (typeof error === 'string') return error;
      // If not string and not null/undefined, assume SerializedError
      return error.message ?? 'Unknown error'; 
  }, []); 
  // --- End of Moved Hooks ---

  // Loading indicator - uses RTK state with improved condition and timeout override
  if (!forceHideLoader && 
     (isLoading === 'pending' || isLoading === 'idle') && 
     !potentialLocations.length) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading Map Data...</span>
      </div>
    );
  }

  // --- Render with Restored Layout ---
  return (
    <div ref={containerRef} className="flex w-full h-full gap-0 p-4 bg-muted/40">
      {/* Sidebar */}
      <div
        style={{ width: `${sidebarWidth}px` }}
        // The className for the container div of the sidebar is now inside MapSidebar.tsx
        // We only need to control the width here.
      >
        <MapSidebar 
          locationsError={locationsError}
          getErrorMessage={getErrorMessage}
          handleManualRefresh={handleManualRefresh}
        />
      </div>

      {/* Resize handle */}
      <div
        className="cursor-col-resize flex items-center justify-center w-4 hover:bg-muted/60 active:bg-muted transition-colors duration-150 rounded-sm mx-1"
        onMouseDown={handleMouseDown}
      >
        <GripVertical className="h-6 w-6 text-muted-foreground" />
      </div>

      {/* Map container */}
      <div className="flex-1 relative h-full rounded-lg overflow-hidden shadow-md border border-border">
        {/* Optional: Add loading overlay specific to map area */}
        {isLoading === 'pending' && !forceHideLoader && (
           <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-sm z-20 rounded-lg">
             <Loader2 className="h-10 w-10 animate-spin text-primary" />
             <span className="ml-3 text-lg font-medium">Loading Map...</span>
           </div>
         )}
        {/* Use try-catch in useEffect to handle map errors */}
        {(() => {
          // Use a self-executing function to wrap our component with error handling
          try {
            return (
              <Map
                key={mapKey}
                mapId={import.meta.env.VITE_GOOGLE_MAP_ID}
                colorScheme={mapOptions.colorScheme}
                defaultCenter={getCenterFromBounds(mapBounds) ?? { lat: 41.89649, lng: -87.63241 }}
                defaultZoom={mapZoom ?? 13}
                gestureHandling={'greedy'}
                disableDefaultUI={true}
                clickableIcons={true}
                onClick={handleMapClick}
                onIdle={() => {
                  console.log("[MapPage] Map idle event - map is ready");
                  setForceHideLoader(true);
                }}
              >
                <MemoizedMarkersComponent />
                <MemoizedPotentialMarkersComponent locations={potentialLocations} />
                <MemoizedMapInfoWindowController />
                <MemoizedMapHandler />
              </Map>
            );
          } catch (error) {
            console.error("[MapPage] Error rendering Map component:", error);
            setForceHideLoader(true);
            return (
              <div className="flex h-full w-full items-center justify-center bg-muted">
                <div className="text-center p-4">
                  <p className="text-xl font-semibold mb-2">Map failed to load</p>
                  <p className="text-sm text-muted-foreground mb-4">There was an error initializing Google Maps</p>
                  <button 
                    className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
                    onClick={() => window.location.reload()}
                  >
                    Refresh page
                  </button>
                </div>
              </div>
            );
          }
        })()}

        {/* Controls on the right side of the Map */}
        <div className="absolute bottom-4 right-4 z-10 space-y-2 flex flex-col items-end">
            <PermissionGuard requiredPermission="admin editor"> { /* Ensure consistent permissions */}
                <MemoizedAlgoliaAdminBox />
            </PermissionGuard>
        </div>

      </div>
    </div>
  );
};

export default MapPage; // Consider React.memo if performance requires it later